import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Paper,
  Divider,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Info,
  Warning,
  Star,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  HelpOutline
} from '@mui/icons-material';
import '../styles/skillHighlighting.css';

// Helper function to convert values to arrays (same as NovaMatchResult)
const toArray = (val) => Array.isArray(val) ? val : (val == null ? [] : [val]);

const SkillHighlighting = ({ scanData, onClose }) => {
  const [highlightTimeout, setHighlightTimeout] = useState(null);
  const resumeRef = useRef(null);
  const jobDescriptionRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeout) {
        clearTimeout(highlightTimeout);
      }
    };
  }, [highlightTimeout]);

  if (!scanData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No scan data available
        </Typography>
      </Box>
    );
  }

  // Extract text data - comprehensive fallbacks including highlightedContent
  const resumeText = scanData.resumeText || 
                    scanData.inputData?.resumeText || 
                    scanData.raw?.resumeText || 
                    scanData.resumeContent ||
                    scanData.extractedText ||
                    scanData.analysisResults?.extractedText ||
                    scanData.result?.extractedText ||
                    scanData.highlightedContent?.highlightedResume ||
                    scanData.highlightedResume ||
                    '';
  const jobDescription = scanData.jobDescription || 
                        scanData.inputData?.jobDescription || 
                        scanData.jdText || 
                        scanData.raw?.jdText || 
                        scanData.jobDescriptionText ||
                        scanData.highlightedContent?.highlightedJobDescription ||
                        scanData.highlightedJobDescription ||
                        '';

  // Skill normalization mapping: used for dedupe and for building highlight patterns.
  // Each key's value is a pipe-separated list of variants to match in document text.
  const skillMapping = {
    'python': 'python3|python 3|py|python programming',
    'sql': 't-sql|pl/sql|structured query language|mysql|postgresql|mssql|oracle sql',
    'power bi': 'ms power bi|powerbi|microsoft power bi|power business intelligence',
    'tableau': 'tableau desktop|tableau server|tableau public',
    'aws': 'amazon web services|amazon aws|aws cloud',
    'azure': 'microsoft azure|azure cloud|ms azure|windows azure',
    'pandas': 'python pandas|pd|pandas library',
    'numpy': 'python numpy|np|numpy library|numerical python',
    'communication': 'communications|verbal communication|written communication|interpersonal skills',
    'project management': 'pm|project manager|pmp|agile project management|waterfall',
    'docker': 'docker container|docker containers|containerization|docker engine',
    'kubernetes': 'k8s|kube|kubernetes orchestration|k8 orchestration',
    'spark': 'apache spark|pyspark|spark streaming|spark sql',
    'hadoop': 'apache hadoop|hadoop ecosystem|hdfs|mapreduce',
    'etl': 'extract transform load|data pipeline|data pipelines|etl pipeline',
    'rest api': 'restful api|rest apis|restful|rest services|web api|restful apis',
    'flask': 'python flask|flask framework|flask web framework',
    'django': 'python django|django framework|django web framework',
    'c': 'c language|c programming|ansi c',
    'r': 'r language|r programming|r statistical programming|r stats',
    'fips': 'fips 140-2|fips 140-3|fips 140|federal information processing standard',
    'javascript': 'javascript|java script|java  script|js|ecmascript',
    'typescript': 'typescript|type script|type  script|ts',
    'cryptography': 'crypto|encryption|pki|public key infrastructure',
    'openssl': 'open ssl|ssl library',
    'cybersecurity': 'cyber security|information security',
    'tls': 'ssl/tls|transport layer security',
    'angular': 'angular|angularjs|angular 2|angular 12|angular framework',
    'git': 'git|github|gitlab|version control',
    'html': 'html|html5|html 5',
    'css': 'css|css3|css 3|scss|sass'
  };

  // Function to normalize skills
  const normalizeSkills = (skills) => {
    if (!Array.isArray(skills)) return [];
    
    return skills.map(skill => {
      if (!skill || typeof skill !== 'string') return skill;
      
      const normalizedSkill = skill.toLowerCase().trim();
      
      // Check if this skill matches any of our mapping keys
      for (const [key, variations] of Object.entries(skillMapping)) {
        const variationList = variations.split('|');
        if (variationList.some(variation => {
          const normalizedVariation = variation.toLowerCase().trim();
          return normalizedSkill === normalizedVariation ||
                 normalizedSkill.includes(normalizedVariation) ||
                 normalizedVariation.includes(normalizedSkill) ||
                 // Handle specific cases like "fips 140-2" matching "fips"
                 (key === 'fips' && normalizedSkill.includes('fips')) ||
                 (key === 'java' && (normalizedSkill.includes('java') || normalizedSkill === 'js')) ||
                 (key === 'javascript' && (normalizedSkill.includes('javascript') || normalizedSkill === 'js' || normalizedSkill.includes('node')));
        })) {
          return key; // Return the normalized key
        }
      }
      
      return normalizedSkill; // Return original if no mapping found
    });
  };

  // Function to deduplicate and normalize skill arrays
  const deduplicateSkills = (skills) => {
    const normalized = normalizeSkills(skills);
    const unique = [...new Set(normalized)];
    return unique.filter(skill => skill && skill.trim() !== '');
  };

  /**
   * Returns regex-safe patterns for highlighting a skill in document text.
   * Includes the display name plus known variants so "JavaScript" also matches "Java Script", "JS", etc.
   * Sorted by length descending so longer phrases match first (e.g. "java script" before "java").
   */
  const getHighlightPatternsForSkill = (skill) => {
    if (!skill || typeof skill !== 'string') return [skill];
    const s = skill.trim();
    const lower = s.toLowerCase();
    const escaped = (str) => (str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const seen = new Set([lower]);
    const raw = [s];
    for (const [key, variations] of Object.entries(skillMapping)) {
      const variationList = variations.split('|').map((v) => v.trim());
      if (lower === key || variationList.some((v) => v.toLowerCase() === lower || lower.includes(v.toLowerCase()) || v.toLowerCase().includes(lower))) {
        variationList.forEach((v) => {
          const vLower = v.toLowerCase();
          if (v && !seen.has(vLower)) {
            seen.add(vLower);
            raw.push(v);
          }
        });
        break;
      }
    }
    const patterns = raw.map((x) => escaped(x)).sort((a, b) => (b.length - a.length));
    return patterns.length ? patterns : [escaped(s)];
  };

  // Extract skill matching data with proper fallbacks - use same logic as Overview tab
  const skillMatching = scanData.skillMatching || scanData.analysisResults?.skillMatching || {};
  const keywordMatches = scanData.keywordMatches || skillMatching.keywordMatches || scanData.analysisResults?.keywordMatches || {};
  const keywords = scanData.keywords || skillMatching.keywords || scanData.analysisResults?.keywords || {};

  // Use the exact same data extraction as Overview tab with backward compatibility
  const matchedSkills = toArray(keywordMatches.mustHaveCovered || keywordMatches.criticalMatchedSkills);
  const missingSkills = toArray(keywordMatches.mustHaveMissing || keywordMatches.criticalMissingSkills);
  const additionalSkills = toArray(keywords.resumeSkills || keywords.jdNiceToHave || []);

  // Debug logging removed

  // Calculate match percentage
  const totalRequired = matchedSkills.length + missingSkills.length;
  const matchPercentage = totalRequired > 0 ? Math.round((matchedSkills.length / totalRequired) * 100) : 0;

  // Format text for display (simplified version of your old working code)
  const formatTextForDisplay = (text, isJobDescription = false) => {
    if (!text) return '';
    
    if (isJobDescription) {
      // For job descriptions, add basic formatting and skill highlighting
      let formattedText = text
        // Add line breaks after bullet points
        .replace(/(•|▪|▫|◦|‣|⁃)/g, '<br/>$1')
        // Add line breaks after periods followed by capital letters (new sentences)
        .replace(/\.\s+([A-Z])/g, '.<br/><br/>$1')
        // Add line breaks after colons (for structured data)
        .replace(/:\s*([A-Z])/g, ':<br/>$1')
        // Clean up multiple line breaks
        .replace(/(<br\/>){3,}/g, '<br/><br/>')
        // Add proper spacing around skill highlights
        .replace(/(<mark[^>]*>)([^<]+)(<\/mark>)/g, ' $1$2$3 ')
        // Ensure proper spacing around text
        .replace(/\s+/g, ' ')
        .trim();
      
      // Add skill highlighting for job description - green (matched) and red (missing)
      // Use variant patterns so e.g. "JavaScript" also highlights "Java Script", "JS" in the text
      matchedSkills.forEach(skill => {
        if (skill && skill.trim()) {
          const patterns = getHighlightPatternsForSkill(skill);
          if (patterns.length) {
            const regex = new RegExp(`\\b(${patterns.join('|')})\\b`, 'gi');
            formattedText = formattedText.replace(regex, '<mark class="skill-green">$1</mark>');
          }
        }
      });
      
      missingSkills.forEach(skill => {
        if (skill && skill.trim()) {
          const patterns = getHighlightPatternsForSkill(skill);
          if (patterns.length) {
            const regex = new RegExp(`\\b(${patterns.join('|')})\\b`, 'gi');
            formattedText = formattedText.replace(regex, '<mark class="skill-red">$1</mark>');
          }
        }
      });
      
      return formattedText;
    } else {
      // For resumes, add section headers and full formatting
      let formattedText = text
        // Fix broken spacing issues first
        .replace(/\s+-\s+/g, ' - ') // Fix "510 - 345 - 8332" spacing
        .replace(/\s+\|\s+/g, ' | ') // Fix "|" spacing
        .replace(/\s+@\s+/g, '@') // Fix email spacing
        .replace(/\s+:\s*\/\//g, '://') // Fix URL spacing
        .replace(/\s+\.\s+com/g, '.com') // Fix domain spacing
        .replace(/\s+-\s+([a-zA-Z])/g, ' - $1') // Fix hyphen spacing
        .replace(/\s+\(\s+/g, ' (') // Fix parenthesis spacing
        .replace(/\s+\)/g, ')') // Fix closing parenthesis spacing
        .replace(/\s+,\s+/g, ', ') // Fix comma spacing
        .replace(/\s+:\s+/g, ': ') // Fix colon spacing
        .replace(/\s+•\s+/g, ' • ') // Fix bullet spacing
        .replace(/\s+-\s+([A-Z])/g, ' - $1') // Fix dash before capital letters
        .replace(/\s+([A-Z])\s+-\s+/g, ' $1 - ') // Fix dash after capital letters
        // Add line breaks after common section headers (only when they appear as standalone headers)
        .replace(/(^|\n)\s*(EDUCATION|EXPERIENCE|SKILLS|PROJECTS|CERTIFICATIONS|AWARDS|SUMMARY|OBJECTIVE|PROFILE)\s*(\n|$)/gi, '$1<br/><br/><strong style="color: #1976d2; font-size: 16px; text-transform: uppercase;">$2</strong><br/>$3')
        // Add line breaks after bullet points
        .replace(/(•|▪|▫|◦|‣|⁃)/g, '<br/>$1')
        // Add line breaks after periods followed by capital letters (new sentences)
        .replace(/\.\s+([A-Z])/g, '.<br/><br/>$1')
        // Add line breaks after colons (for structured data)
        .replace(/:\s*([A-Z])/g, ':<br/>$1')
        // Add line breaks after common separators
        .replace(/(\d{4})\s*-\s*(\d{4}|Present)/g, '<br/>$1 - $2')
        .replace(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}/gi, '<br/>$&')
        // Clean up multiple line breaks
        .replace(/(<br\/>){3,}/g, '<br/><br/>')
        // Add proper spacing around skill highlights
        .replace(/(<mark[^>]*>)([^<]+)(<\/mark>)/g, ' $1$2$3 ')
        // Ensure proper spacing around text
        .replace(/\s+/g, ' ')
        .trim();
      
      // Add skill highlighting for resume - only green (matched skills from resume)
      // Note: Missing skills (red) should NOT appear in resume since they're not in the resume
      // Use variant patterns so e.g. "JavaScript" also highlights "Java Script", "JS" in the text
      matchedSkills.forEach(skill => {
        if (skill && skill.trim()) {
          const patterns = getHighlightPatternsForSkill(skill);
          if (patterns.length) {
            const regex = new RegExp(`\\b(${patterns.join('|')})\\b`, 'gi');
            formattedText = formattedText.replace(regex, '<mark class="skill-green">$1</mark>');
          }
        }
      });
      
      return formattedText;
    }
  };

  // Interactive highlighting
  const highlightTerm = (term) => {
    if (!term || term.trim() === '') return;
    
    
    
    if (highlightTimeout) {
      clearTimeout(highlightTimeout);
    }
    
    addGoldHighlighting(term);
    
    // Auto-scroll to show matches with a small delay to ensure highlighting is applied
    setTimeout(() => {
    scrollToMatches(term);
    }, 100);
    
    const timeout = setTimeout(() => {
      clearGoldHighlighting();
    }, 3000);
    
    setHighlightTimeout(timeout);
  };

  const addGoldHighlighting = (term) => {
    const resumeElement = resumeRef.current;
    const jobDescriptionElement = jobDescriptionRef.current;
    
    if (!resumeElement || !jobDescriptionElement) {
      return;
    }
    
    // Use same variant patterns as static highlights so "JavaScript" highlights "Java Script", "JS", etc.
    const patterns = getHighlightPatternsForSkill(term);
    const patternStr = patterns.length ? patterns.join('|') : term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(\\b|^|\\s)(${patternStr})(\\b|$|\\s)`, 'gi');
    highlightTextInElement(resumeElement, regex);
    highlightTextInElement(jobDescriptionElement, regex);
  };

  const highlightTextInElement = (element, regex) => {
    if (!element) return;
    
    let html = element.innerHTML;
    const originalLength = html.length;
    
    // Remove any existing highlights first
    html = html.replace(/<span class="interactive-highlight"[^>]*>(.*?)<\/span>/gi, '$1');
    
    // Apply new highlights with the improved regex
    const beforeReplace = html;
    html = html.replace(regex, '$1<span class="interactive-highlight">$2</span>$3');
    
    
    
    element.innerHTML = html;
  };

  const clearGoldHighlighting = () => {
    const resumeElement = resumeRef.current;
    const jobDescriptionElement = jobDescriptionRef.current;
    
    if (resumeElement) {
      let html = resumeElement.innerHTML;
      html = html.replace(/<span class="interactive-highlight"[^>]*>(.*?)<\/span>/gi, '$1');
      resumeElement.innerHTML = html;
    }
    
    if (jobDescriptionElement) {
      let html = jobDescriptionElement.innerHTML;
      html = html.replace(/<span class="interactive-highlight"[^>]*>(.*?)<\/span>/gi, '$1');
      jobDescriptionElement.innerHTML = html;
    }
  };

  const scrollToMatches = (term) => {
    const resumeElement = resumeRef.current;
    const jobDescriptionElement = jobDescriptionRef.current;
    
    if (!resumeElement || !jobDescriptionElement) {
      return;
    }
    
    // Find first occurrence in resume and scroll to it
    const resumeHighlights = resumeElement.querySelectorAll('.interactive-highlight');
    if (resumeHighlights.length > 0) {
      resumeHighlights[0].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
    
    // Find first occurrence in job description and scroll to it
    const jobHighlights = jobDescriptionElement.querySelectorAll('.interactive-highlight');
    if (jobHighlights.length > 0) {
      jobHighlights[0].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
  };


  return (
    <Box sx={{ p: { xs: 2, sm: 2, md: 3 }, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1 }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          color: 'primary.main', 
          fontSize: { xs: '1.5rem', sm: '2rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <BusinessIcon />
          Document Analysis
        </Typography>
        {onClose && (
          <Button 
            onClick={onClose} 
            variant="outlined" 
            size="large" 
            sx={{ 
              minWidth: 100,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2
            }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        )}
      </Box>

      {/* Summary Stats - Floating Cards Style */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
          <TrendingUpIcon />
          Skill Analysis Overview
        </Typography>
        <Box sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'center'
        }}>
          <Card sx={{ 
            p: 2.5, 
            textAlign: 'center', 
            minWidth: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5, color: 'primary.main', fontSize: '2.5rem' }}>
              {toArray(keywords?.jdMustHave).length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem' }}>Required Skills (From Job Description)</Typography>
          </Card>
          <Card sx={{ 
            p: 2.5, 
            textAlign: 'center', 
            minWidth: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main',
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5, color: 'error.main', fontSize: '2.5rem' }}>
              {missingSkills.length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem' }}>Skills Missing (From Candidate Resume)</Typography>
          </Card>
          <Card sx={{ 
            p: 2.5, 
            textAlign: 'center', 
            minWidth: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'success.main',
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5, color: 'success.main', fontSize: '2.5rem' }}>
              {matchedSkills.length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.85rem' }}>Skills Matched (From Candidate Resume)</Typography>
          </Card>
          <Card sx={{ 
            p: 2.5, 
            textAlign: 'center', 
            minWidth: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
            borderRadius: 3,
            border: '2px solid',
            borderColor: matchPercentage >= 70 ? 'success.main' : matchPercentage >= 50 ? 'warning.main' : 'error.main',
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }
          }}>
            <Typography variant="h3" sx={{ 
              fontWeight: 800, 
              mb: 0.5, 
              fontSize: '2.5rem',
              color: matchPercentage >= 70 ? 'success.main' : matchPercentage >= 50 ? 'warning.main' : 'error.main'
            }}>
              {matchPercentage}%
            </Typography>
            <Typography variant="body2" sx={{ 
              fontWeight: 600, 
              fontSize: '0.85rem',
              color: 'text.secondary'
            }}>Match Rate</Typography>
          </Card>
        </Box>
      </Box>

      {/* Document Content - Floating Cards Style */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
          <StarIcon />
          Document Analysis
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 3,
          height: { xs: '65vh', sm: '70vh', md: '75vh' }
        }}>
          {/* Resume Content */}
          <Box sx={{ flex: 1, minHeight: { xs: '300px', lg: 'auto' } }}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'primary.main', 
                color: 'white',
                flexShrink: 0
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                  <BusinessIcon fontSize="small" />
                  Resume Content
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5, fontSize: '0.75rem' }}>
                  {resumeText ? `${resumeText.length} characters` : 'No content available'}
                </Typography>
              </Box>
              <Box
                ref={resumeRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 3,
                  bgcolor: 'white',
                  fontSize: '15px',
                  lineHeight: 1.8,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  color: '#2c3e50'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: resumeText ? formatTextForDisplay(resumeText, false) : 
                    `<div style="text-align: center; color: #666; font-style: italic; padding: 40px; font-size: 16px;">
                      No resume content available. Please check the data structure.<br/><br/>
                      <strong>Debug Info:</strong><br/>
                      Available keys: ${Object.keys(scanData).join(', ')}<br/>
                      Resume text length: ${resumeText.length}<br/>
                      Job description length: ${jobDescription.length}
                    </div>`
                }}
              />
            </Card>
          </Box>

          {/* Job Description */}
          <Box sx={{ flex: 1, minHeight: { xs: '300px', lg: 'auto' } }}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'success.main', 
                color: 'white',
                flexShrink: 0
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
                  <TrendingUpIcon fontSize="small" />
                  Job Description
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, mt: 0.5, fontSize: '0.75rem' }}>
                  {jobDescription ? `${jobDescription.length} characters` : 'No content available'}
                </Typography>
              </Box>
              <Box
                ref={jobDescriptionRef}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 3,
                  bgcolor: 'white',
                  fontSize: '15px',
                  lineHeight: 1.8,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  color: '#2c3e50'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: jobDescription ? formatTextForDisplay(jobDescription, true) : 
                    `<div style="text-align: center; color: #666; font-style: italic; padding: 40px; font-size: 16px;">
                      No job description available.<br/><br/>
                      <strong>Debug Info:</strong><br/>
                      Available keys: ${Object.keys(scanData).join(', ')}<br/>
                      Job description length: ${jobDescription.length}
                    </div>`
                }}
              />
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Skills Legend - Compact Info Icon */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                Skill Categories Explained:
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CheckCircle sx={{ color: '#1976d2', mr: 1, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    Required Skills (From Job Description):
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 3 }}>
                  Critical skills required for the role from job description
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Error sx={{ color: '#d32f2f', mr: 1, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    Skills Missing (From Candidate Resume):
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 3 }}>
                  Required skills from the job that are not found in the candidate's resume
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CheckCircle sx={{ color: '#388e3c', mr: 1, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    Skills Matched (From Candidate Resume):
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.8rem', ml: 3 }}>
                  Required skills from JD that are found in the candidate's resume
                </Typography>
              </Box>
            </Box>
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(0, 0, 0, 0.9)',
                maxWidth: 300,
                fontSize: '0.8rem'
              }
            }
          }}
        >
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <HelpOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Skills Sections - Floating Cards Style */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
          <TrendingUpIcon />
          Skills Breakdown
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              p: 2, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'success.main',
              bgcolor: 'white',
              height: '100%',
              maxHeight: 220
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.95rem' }}>
                  Skills Matched (From Candidate Resume)
                </Typography>
                <Box sx={{ 
                  ml: 1, 
                  px: 1.5, 
                  py: 0.25, 
                  bgcolor: 'success.main', 
                  color: 'white',
                  borderRadius: 2,
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}>
                  {matchedSkills.length}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: '60px', maxHeight: '140px', overflowY: 'auto' }}>
                {matchedSkills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    color="success"
                    size="small"
                    onClick={() => highlightTerm(skill)}
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { transform: 'scale(1.05)' },
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      borderRadius: 2
                    }}
                  />
                ))}
                {matchedSkills.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontStyle: 'italic', 
                    textAlign: 'center',
                    width: '100%',
                    py: 3,
                    fontSize: '0.9rem'
                  }}>
                    No matched skills found
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ 
              p: 2.5, 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'error.main',
              bgcolor: 'white',
              height: '100%',
              maxHeight: 280
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Error sx={{ color: 'error.main', mr: 1, fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                  Skills Missing (From Candidate Resume)
                </Typography>
                <Box sx={{ 
                  ml: 1, 
                  px: 2, 
                  py: 0.5, 
                  bgcolor: 'error.main', 
                  color: 'white',
                  borderRadius: 2,
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}>
                  {missingSkills.length}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: '80px', maxHeight: '180px', overflowY: 'auto' }}>
                {missingSkills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    color="error"
                    size="small"
                    onClick={() => highlightTerm(skill)}
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { transform: 'scale(1.05)' },
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      borderRadius: 2
                    }}
                  />
                ))}
                {missingSkills.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontStyle: 'italic', 
                    textAlign: 'center',
                    width: '100%',
                    py: 3,
                    fontSize: '0.9rem'
                  }}>
                    All required skills found
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ 
        textAlign: 'center', 
        p: 3,
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <Info sx={{ fontSize: 20 }} />
          Click on any skill to highlight it in both documents
        </Typography>
      </Box>
    </Box>
  );
};

export default SkillHighlighting;